import { createRouter, createWebHistory, createMemoryHistory, RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '../stores/auth'

// Définition des routes de l'application
export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('../layouts/PublicLayout.vue'),
    children: [
      // 1. Pages à segments fixes spécifiques (region, bundesland)
      {
        path: ':typeSlug/region/:regionSlug',
        name: 'public-region-page',
        component: () => import('../pages/public/RegionPage.vue'),
        meta: { isPublic: true }
      },
      {
        path: ':typeSlug/bundesland/:regionSlug',
        name: 'public-region-page-de',
        component: () => import('../pages/public/RegionPage.vue'),
        meta: { isPublic: true }
      },

      // 2. Détail d'un établissement avec tous les niveaux
      {
        path: ':typeSlug/:parentSlug/:entitySlug/:slug',
        name: 'public-facility-detail-extended',
        component: () => import('../pages/public/FacilityDetail.vue'),
        meta: { isPublic: true }
      },

      // 3. Listing avec pagination ville
      {
        path: ':typeSlug/:entitySlug/:citySlug/:page(\\d+)',
        name: 'public-facility-listing-city-page',
        component: () => import('../pages/public/FacilityListing.vue'),
        meta: { isPublic: true }
      },

      // 4. Listing avec pagination simple
      {
        path: ':typeSlug/:entitySlug/:page(\\d+)',
        name: 'public-facility-listing-page',
        component: () => import('../pages/public/FacilityListing.vue'),
        meta: { isPublic: true }
      },

      // 5. Listing par ville sans pagination
      {
        path: ':typeSlug/:entitySlug/:citySlug',
        name: 'public-facility-listing-city',
        component: () => import('../pages/public/FacilityListing.vue'),
        meta: { isPublic: true }
      },

      // 6. Listing simple par slug
      {
        path: ':typeSlug/:entitySlug',
        name: 'public-facility-listing',
        component: () => import('../pages/public/FacilityListing.vue'),
        meta: { isPublic: true }
      },

      // 7. Page d'un type d'établissement (comme /ehpad)
      {
        path: ':slug',
        name: 'public-facility-type',
        component: () => import('../pages/public/FacilityType.vue'),
        meta: { isPublic: true }
      },

      // 8. Page d'accueil
      {
        path: '',
        name: 'public-home',
        component: () => import('../pages/Login.vue'),
        meta: { isPublic: true }
      },

      // 9. Listing global des établissements (ex: /facilities)
      {
        path: 'facilities',
        name: 'public-facilities',
        component: () => import('../pages/public/FacilityList.vue'),
        meta: { isPublic: true }
      }
    ]
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('../pages/Login.vue'),
    meta: { isPublic: true }
  },
  {
    path: '/facility',
    component: () => import('../layouts/FacilityLayout.vue'),
    meta: { requiresAuth: true, role: 'facility_user', isPublic: false },
    children: [
      {
        path: '',
        name: 'facility-dashboard',
        component: () => import('../pages/facility/index.vue'),
        meta: { isPublic: false }
      },
      {
        path: 'info',
        name: 'facility-info',
        component: () => import('../pages/facility/info.vue'),
        meta: { isPublic: false }
      },
      {
        path: 'contacts',
        name: 'facility-contacts',
        component: () => import('../pages/facility/contacts.vue'),
        meta: { isPublic: false }
      },
      {
        path: 'housing',
        name: 'facility-housing',
        component: () => import('../pages/facility/housing.vue'),
        meta: { isPublic: false }
      },
      {
        path: 'features',
        name: 'facility-features',
        redirect: to => {
          // Cette redirection sera résolue dynamiquement dans FacilityLayout.vue
          // via la fonction loadFeatureCategories
          return { name: 'facility-dashboard' }
        },
        meta: { isPublic: false }
      },
      {
        path: 'features/:categoryId',
        name: 'facility-features-category',
        component: () => import('../pages/facility/features.vue'),
        meta: { isPublic: false }
      },
      {
        path: 'media',
        name: 'facility-media',
        component: () => import('../pages/facility/media.vue'),
        meta: { isPublic: false }
      },
      {
        path: 'documents',
        name: 'facility-documents',
        component: () => import('../pages/facility/documents.vue'),
        meta: { isPublic: false }
      }
      ,
      {
        path: 'leads',
        name: 'facility-leads',
        component: () => import('../pages/facility/leads.vue'),
        meta: { isPublic: false }
      }
    ]
  },
  {
    path: '/adminConfig',
    component: () => import('../layouts/AdminLayout.vue'),
    meta: { requiresAuth: true, role: 'admin_global', isPublic: false },
    children: [
      {
        path: '',
        name: 'admin-dashboard',
        component: () => import('../pages/adminConfig/index.vue'),
        meta: { isPublic: false }
      },
      {
        path: 'countries',
        name: 'admin-countries',
        component: () => import('../pages/adminConfig/countries/index.vue'),
        meta: { isPublic: false }
      },
      {
        path: 'countries/new',
        name: 'admin-countries-new',
        component: () => import('../pages/adminConfig/countries/new.vue'),
        meta: { isPublic: false }
      },
      {
        path: 'countries/:id/edit',
        name: 'admin-countries-edit',
        component: () => import('../pages/adminConfig/countries/[id]/edit.vue'),
        meta: { isPublic: false }
      },
      {
        path: 'geo-levels',
        name: 'admin-geo-levels',
        component: () => import('../pages/adminConfig/geo-levels/index.vue'),
        meta: { isPublic: false }
      },
      {
        path: 'geo-levels/new',
        name: 'admin-geo-levels-new',
        component: () => import('../pages/adminConfig/geo-levels/new.vue'),
        meta: { isPublic: false }
      },
      {
        path: 'geo-levels/:id/edit',
        name: 'admin-geo-levels-edit',
        component: () => import('../pages/adminConfig/geo-levels/[id]/edit.vue'),
        meta: { isPublic: false }
      },
      {
        path: 'facility-types',
        name: 'admin-facility-types',
        component: () => import('../pages/adminConfig/facility-types/index.vue'),
        meta: { isPublic: false }
      },
      {
        path: 'facility-types/new',
        name: 'admin-facility-types-new',
        component: () => import('../pages/adminConfig/facility-types/new.vue'),
        meta: { isPublic: false }
      },
      {
        path: 'facility-types/:id/edit',
        name: 'admin-facility-types-edit',
        component: () => import('../pages/adminConfig/facility-types/[id]/edit.vue'),
        meta: { isPublic: false }
      },
      {
        path: 'features',
        name: 'admin-features',
        component: () => import('../pages/adminConfig/features/index.vue'),
        meta: { isPublic: false }
      },
      {
        path: 'groups',
        name: 'admin-groups',
        component: () => import('../pages/adminConfig/groups/index.vue'),
        meta: { isPublic: false }
      },
      {
        path: 'groups/new',
        name: 'admin-groups-new',
        component: () => import('../pages/adminConfig/groups/new.vue'),
        meta: { isPublic: false }
      },
      {
        path: 'groups/:id/edit',
        name: 'admin-groups-edit',
        component: () => import('../pages/adminConfig/groups/[id]/edit.vue'),
        meta: { isPublic: false }
      },
      {
        path: 'groups/:id/users',
        name: 'admin-groups-users',
        component: () => import('../pages/adminConfig/groups/[id]/users.vue'),
        meta: { isPublic: false }
      },
      {
        path: 'housing-types',
        name: 'admin-housing-types',
        component: () => import('../pages/adminConfig/housing-types/index.vue'),
        meta: { isPublic: false }
      },
      {
        path: 'housing-types/:id/edit',
        name: 'admin-housing-types-edit',
        component: () => import('../pages/adminConfig/housing-types/[id]/edit.vue'),
        meta: { isPublic: false }
      },
      {
        path: 'users',
        name: 'admin-users',
        component: () => import('../pages/adminConfig/users/index.vue'),
        meta: { isPublic: false }
      },
      {
        path: 'lead-forms',
        name: 'admin-lead-forms',
        component: () => import('../pages/adminConfig/lead-forms/index.vue'),
        meta: { isPublic: false }
      }
    ]
  },
  {
    path: '/adminGestion',
    component: () => import('../layouts/AdminGestionLayout.vue'),
    meta: { requiresAuth: true, role: ['admin_global', 'admin_secretaire'], isPublic: false },
    children: [
      {
        path: '',
        name: 'admin-gestion-dashboard',
        component: () => import('../pages/adminGestion/index.vue'),
        props: true,
        meta: { isPublic: false }
      },
      {
        path: 'groups',
        name: 'admin-gestion-groups',
        component: () => import('../pages/adminGestion/groups/index.vue'),
        props: true,
        meta: { isPublic: false }
      },
      {
        path: 'facilities',
        name: 'admin-gestion-facilities',
        component: () => import('../pages/adminGestion/facilities/index.vue'),
        props: true,
        meta: { isPublic: false }
      },
      {
        path: 'facilities/:id',
        name: 'admin-gestion-facility-detail',
        component: () => import('../pages/adminGestion/facilities/[id].vue'),
        props: true,
        meta: { isPublic: false }
      },
      {
        path: 'redaction/dossiers',
        name: 'admin-gestion-dossiers',
        component: () => import('../pages/adminGestion/redaction/dossiers.vue'),
        props: true,
        meta: { isPublic: false }
      },
      {
        path: 'redaction/blog',
        name: 'admin-gestion-blog',
        component: () => import('../pages/adminGestion/redaction/blog.vue'),
        props: true,
        meta: { isPublic: false }
      }
    ]
  },
  {
    path: '/commercial',
    component: () => import('../layouts/CommercialLayout.vue'),
    meta: { requiresAuth: true, role: 'commercial', isPublic: false },
    children: [
      {
        path: '',
        name: 'commercial-dashboard',
        component: () => import('../pages/commercial/index.vue'),
        meta: { isPublic: false }
      },
      {
        path: 'leads',
        name: 'commercial-leads',
        component: () => import('../pages/commercial/leads.vue'),
        meta: { isPublic: false }
      },
      {
        path: 'lead/:id',
        name: 'commercial-lead-detail',
        component: () => import('../pages/commercial/lead.vue'),
        props: true,
        meta: { isPublic: false }
      },
      {
        path: 'facilities',
        name: 'commercial-facilities',
        component: () => import('../pages/commercial/facilities.vue'),
        meta: { isPublic: false }
      }
    ]
  },
  {
    path: '/commercial/map',
    name: 'CommercialMap',
    component: () => import('../pages/commercial/map.vue'),
    meta: { requiresAuth: true, role: 'commercial', isPublic: false }
  },
  {
    path: '/group',
    component: () => import('../layouts/GroupLayout.vue'),
    meta: { requiresAuth: true, role: 'groupe_user', isPublic: false },
    children: [
        {
          path: '',
          name: 'group-dashboard',
          component: () => import('../pages/group/index.vue'),
          meta: { isPublic: false } 
      },
      {
        path: 'leads',
        name: 'group-leads',
        component: () => import('../pages/group/leads.vue'),
        meta: { isPublic: false }
      },
      {
        path: 'facilities',
        name: 'group-facilities',
        component: () => import('../pages/group/facilities.vue'),
        meta: { isPublic: false }
      },
      {
        path: 'reporting',
        name: 'group-reporting',
        component: () => import('../pages/group/reporting.vue'),
        meta: { isPublic: false }
      },

    ]
  }
]

const isClient = typeof window !== 'undefined'

const router = createRouter({
  history: isClient ? createWebHistory() : createMemoryHistory(),
  routes
})

// Navigation guard
router.beforeEach(async (to, from, next) => {
  const auth = useAuthStore()

  // Wait for initial auth check
  if (auth.loading) {
    await auth.loadUser()
  }

  // Skip auth check for public routes
  if (!to.meta.requiresAuth) {
    next()
    return
  }

  // Check if user is authenticated
  if (!auth.isAuthenticated) {
    next('/')
    return
  }

  // Check role-based access
  if (to.meta.role) {
    const allowedRoles = Array.isArray(to.meta.role) ? to.meta.role : [to.meta.role]
    if (!allowedRoles.includes(auth.userRole)) {
      next(auth.getRedirectPath())
      return
    }
  }

  next()
})

export default router